/**
 * @file pages/Setup.jsx
 * @description First-run onboarding wizard. Public route (no ProtectedRoute) —
 * guides an admin through: database + Azure config, schema init, sign-in, and
 * promoting themselves to the first admin.
 *
 * All write steps require the setup token printed to the backend console at boot.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { loginRequest } from '../auth/msalConfig';
import { getSetupStatus, saveSetupEnv, initSetupDb, promoteSetupAdmin } from '../api/setup';

const EMPTY_ENV_FORM = {
  dbHost: 'localhost', dbPort: '3306', dbName: 'netapi', dbUser: 'root', dbPass: '',
  azureTenantId: '', azureClientId: '', azureAudience: '',
  // Default to the Vite dev server's own origin, not window.location.origin — if this
  // page is ever reached through a proxy or a different port, that would silently
  // write the wrong redirect URI (must match the frontend, not the backend API).
  frontendRedirectUri: 'http://localhost:5173', azureApiScope: '',
};

export default function Setup() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isAuthenticated = useIsAuthenticated();
  const { instance } = useMsal();

  const [setupToken, setSetupToken] = useState('');
  const [envForm, setEnvForm] = useState(EMPTY_ENV_FORM);

  const { data: status, isLoading } = useQuery({
    queryKey: ['setupStatus'],
    queryFn: getSetupStatus,
    refetchInterval: 5000, // poll — DB/schema state can change from actions taken here
  });

  const envMutation = useMutation({
    mutationFn: () => saveSetupEnv(setupToken, {
      db: { host: envForm.dbHost, port: envForm.dbPort, name: envForm.dbName, user: envForm.dbUser, pass: envForm.dbPass },
      azure: {
        tenantId: envForm.azureTenantId, clientId: envForm.azureClientId, audience: envForm.azureAudience,
        frontendRedirectUri: envForm.frontendRedirectUri, apiScope: envForm.azureApiScope,
      },
    }),
  });

  const initDbMutation = useMutation({
    mutationFn: () => initSetupDb(setupToken),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['setupStatus'] }),
  });

  const promoteMutation = useMutation({
    mutationFn: () => promoteSetupAdmin(setupToken),
    onSuccess: () => navigate('/', { replace: true }),
  });

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="spinner-border" style={{ color: 'var(--color-primary)' }} role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  if (status?.setupComplete) {
    return (
      <div className="page-content text-center py-5">
        <i className="bi bi-check-circle-fill fs-1" style={{ color: 'var(--color-accent-2)' }} />
        <h1 className="h4 fw-bold mt-3">Setup already complete</h1>
        <p className="text-muted">An admin account already exists for this installation.</p>
        <a href="/login" className="btn" style={{ backgroundColor: 'var(--color-primary)', color: '#fff' }}>
          Go to sign in
        </a>
      </div>
    );
  }

  return (
    <div className="page-content" style={{ maxWidth: 720, margin: '0 auto' }}>
      <h1 className="h4 fw-bold mb-4" style={{ color: 'var(--color-primary)' }}>
        <i className="bi bi-rocket-takeoff me-2" aria-hidden="true" />
        NET-API First-Run Setup
      </h1>

      {/* Setup token */}
      <div className="card mb-4">
        <div className="card-header fw-semibold" style={{ backgroundColor: 'var(--color-primary)', color: '#fff' }}>
          Setup Token
        </div>
        <div className="card-body">
          <p className="text-muted small">
            Printed to the backend console at boot (also saved to <code>backend/.setup-token</code>). Paste it here to unlock the steps below.
          </p>
          <input
            type="text"
            className="form-control font-monospace"
            value={setupToken}
            onChange={(e) => setSetupToken(e.target.value)}
            placeholder="paste setup token here"
          />
        </div>
      </div>

      {/* Step 1: DB + Azure config */}
      <div className="card mb-4">
        <div className="card-header fw-semibold" style={{ backgroundColor: 'var(--color-primary)', color: '#fff' }}>
          Step 1 · Database &amp; Azure Entra ID Configuration
        </div>
        <div className="card-body">
          <p className={`small ${status?.dbReachable ? 'text-success' : 'text-danger'}`}>
            <i className={`bi ${status?.dbReachable ? 'bi-check-circle' : 'bi-x-circle'} me-1`} />
            Database currently {status?.dbReachable ? 'reachable' : 'unreachable'}
          </p>

          <div className="row g-2 mb-3">
            <div className="col-6"><label className="form-label small fw-semibold">DB Host</label>
              <input className="form-control form-control-sm" value={envForm.dbHost} onChange={(e) => setEnvForm((p) => ({ ...p, dbHost: e.target.value }))} /></div>
            <div className="col-6"><label className="form-label small fw-semibold">DB Port</label>
              <input className="form-control form-control-sm" value={envForm.dbPort} onChange={(e) => setEnvForm((p) => ({ ...p, dbPort: e.target.value }))} /></div>
            <div className="col-6"><label className="form-label small fw-semibold">DB Name</label>
              <input className="form-control form-control-sm" value={envForm.dbName} onChange={(e) => setEnvForm((p) => ({ ...p, dbName: e.target.value }))} /></div>
            <div className="col-6"><label className="form-label small fw-semibold">DB User</label>
              <input className="form-control form-control-sm" value={envForm.dbUser} onChange={(e) => setEnvForm((p) => ({ ...p, dbUser: e.target.value }))} /></div>
            <div className="col-12"><label className="form-label small fw-semibold">DB Password</label>
              <input type="password" className="form-control form-control-sm" value={envForm.dbPass} onChange={(e) => setEnvForm((p) => ({ ...p, dbPass: e.target.value }))} /></div>
            <div className="col-6"><label className="form-label small fw-semibold">Azure Tenant ID</label>
              <input className="form-control form-control-sm" value={envForm.azureTenantId} onChange={(e) => setEnvForm((p) => ({ ...p, azureTenantId: e.target.value }))} /></div>
            <div className="col-6"><label className="form-label small fw-semibold">Azure Client ID</label>
              <input className="form-control form-control-sm" value={envForm.azureClientId} onChange={(e) => setEnvForm((p) => ({ ...p, azureClientId: e.target.value }))} /></div>
            <div className="col-6"><label className="form-label small fw-semibold">API Audience</label>
              <input className="form-control form-control-sm" value={envForm.azureAudience} onChange={(e) => setEnvForm((p) => ({ ...p, azureAudience: e.target.value }))} placeholder="api://client-id" /></div>
            <div className="col-6"><label className="form-label small fw-semibold">API Scope</label>
              <input className="form-control form-control-sm" value={envForm.azureApiScope} onChange={(e) => setEnvForm((p) => ({ ...p, azureApiScope: e.target.value }))} placeholder="api://client-id/access_as_user" /></div>
            <div className="col-12">
              <label className="form-label small fw-semibold">Frontend Redirect URI</label>
              <input className="form-control form-control-sm" value={envForm.frontendRedirectUri} onChange={(e) => setEnvForm((p) => ({ ...p, frontendRedirectUri: e.target.value }))} placeholder="http://localhost:5173" />
              <div className="form-text">
                Must be the <strong>frontend</strong> URL (this app's own address), not the backend API port.
                Also add this exact URL to your Azure app registration as a Single-page application redirect URI.
              </div>
            </div>
          </div>

          <p className="text-muted small">
            <i className="bi bi-info-circle me-1" aria-hidden="true" />
            Fields are never pre-filled from saved values (passwords and secrets aren't
            sent back to the browser) — this form is write-only. After saving, restart
            the backend and reload this page before continuing to Step 3.
          </p>

          {envMutation.isError && <div className="alert alert-danger small">{envMutation.error.message}</div>}
          {envMutation.isSuccess && (
            <div className="alert alert-success small">
              {envMutation.data.message}
              <button
                type="button"
                className="btn btn-sm btn-outline-success ms-2"
                onClick={() => window.location.reload()}
              >
                Reload this page
              </button>
            </div>
          )}

          <button
            className="btn btn-sm"
            style={{ backgroundColor: 'var(--color-accent-1)', color: '#000' }}
            disabled={!setupToken || envMutation.isPending}
            onClick={() => envMutation.mutate()}
          >
            Save Configuration
          </button>
        </div>
      </div>

      {/* Step 2: Init DB */}
      <div className="card mb-4">
        <div className="card-header fw-semibold" style={{ backgroundColor: 'var(--color-primary)', color: '#fff' }}>
          Step 2 · Initialize Database Schema
        </div>
        <div className="card-body">
          <p className={`small ${status?.schemaReady ? 'text-success' : 'text-muted'}`}>
            <i className={`bi ${status?.schemaReady ? 'bi-check-circle text-success' : 'bi-dash-circle'} me-1`} />
            {status?.schemaReady ? 'Tables already created' : 'Tables not created yet'}
          </p>
          {initDbMutation.isError && <div className="alert alert-danger small">{initDbMutation.error.message}</div>}
          {initDbMutation.isSuccess && (
            <div className="alert alert-success small">
              {initDbMutation.data.message} ({initDbMutation.data.tables.length} tables)
            </div>
          )}
          <button
            className="btn btn-sm"
            style={{ backgroundColor: 'var(--color-accent-1)', color: '#000' }}
            disabled={!setupToken || !status?.dbReachable || initDbMutation.isPending}
            onClick={() => initDbMutation.mutate()}
          >
            Initialize Database
          </button>
        </div>
      </div>

      {/* Step 3: Sign in */}
      <div className="card mb-4">
        <div className="card-header fw-semibold" style={{ backgroundColor: 'var(--color-primary)', color: '#fff' }}>
          Step 3 · Sign In
        </div>
        <div className="card-body">
          <p className="text-muted small">Sign in with Microsoft — this creates your local user record.</p>
          <button
            className="btn btn-sm"
            style={{ backgroundColor: 'var(--color-accent-1)', color: '#000' }}
            disabled={isAuthenticated || !status?.schemaReady}
            onClick={() => instance.loginRedirect(loginRequest)}
          >
            {isAuthenticated ? 'Signed in' : 'Sign in with Microsoft'}
          </button>
        </div>
      </div>

      {/* Step 4: Promote to admin */}
      <div className="card mb-4">
        <div className="card-header fw-semibold" style={{ backgroundColor: 'var(--color-primary)', color: '#fff' }}>
          Step 4 · Become Admin
        </div>
        <div className="card-body">
          <p className="text-muted small">Promotes your signed-in account to admin and finishes setup.</p>
          {promoteMutation.isError && <div className="alert alert-danger small">{promoteMutation.error.message}</div>}
          <button
            className="btn btn-sm"
            style={{ backgroundColor: 'var(--color-primary)', color: '#fff' }}
            disabled={!setupToken || !isAuthenticated || status?.adminExists || promoteMutation.isPending}
            onClick={() => promoteMutation.mutate()}
          >
            Promote Myself to Admin
          </button>
        </div>
      </div>
    </div>
  );
}
